import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { FormControl, Select, MenuItem, CircularProgress, Box, Button, Typography } from '@material-ui/core';
import BreadcrumbLayout from '../../../shared/components/common/breadcrumbLayout';
import ClientHelpModal from '../../clientHelpModal/clientHelpModal';
import { FeedbackState } from '../../../shared/ui';
import { CatalogHierarchyService } from '../../../shared/service/catalogHierarchyService';
import styled, { keyframes } from 'styled-components';
import {
    CartShortcutButton,
    ContentAlignedContainer,
    MainContent,
    SearchHeaderSection,
    SearchHero,
    SearchHeroTitle,
    ServiceCardTitle,
    ServicesGrid,
} from '../styles';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BuilderContainer = styled(Box)`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px 16px;
`;

const BuilderCard = styled(Box)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  padding: 40px;
  width: 100%;
  max-width: 680px;
  border: 1px solid rgba(229, 231, 235, 0.8);
  animation: ${fadeIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const StepHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const StepTitle = styled(Typography)`
  && {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 8px;
    font-family: 'Inter', 'Roboto', sans-serif;
  }
`;

const StepSubtitle = styled(Typography)`
  && {
    font-size: 0.95rem;
    color: #6B7280;
    font-weight: 400;
  }
`;

const FormRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease-out forwards;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledFormControl = styled(FormControl)`
  && {
    background-color: #F9FAFB;
    border-radius: 12px;
    
    .MuiOutlinedInput-root {
      border-radius: 12px;
      transition: all 0.2s ease;
      
      &:hover fieldset {
        border-color: #9CA3AF;
      }
      &.Mui-focused fieldset {
        border-color: #2563EB;
        border-width: 2px;
      }
    }
  }
`;

const ContinueButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
    color: white;
    font-weight: 700;
    padding: 14px 0;
    border-radius: 12px;
    text-transform: none;
    font-size: 1.1rem;
    font-family: 'Inter', 'Roboto', sans-serif;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: 16px;
    width: 100%;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
      background: linear-gradient(135deg, #1D4ED8 0%, #1e40af 100%);
    }
    
    &:disabled {
      background: #E5E7EB;
      color: #9CA3AF;
      box-shadow: none;
      transform: none;
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#2563EB' : '#E5E7EB'};
  color: ${props => props.active ? 'white' : '#9CA3AF'};
  font-size: 0.75rem;
  font-weight: bold;
`;


function ClientQuoteSearchView({
    cartItemCount,
    onServiceSelect,
    onViewCart,
    loading,
    error,
    currentLanguage,
}) {
    const clientLanguage = currentLanguage.bepiService.client;
    const sharedLanguage = currentLanguage.bepiService.shared;

    // Estados para hierarquia
    const [categorias, setCategorias] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [servicos, setServicos] = useState([]);
    
    const [loadingCategorias, setLoadingCategorias] = useState(false);
    const [loadingProdutos, setLoadingProdutos] = useState(false);
    const [loadingServicos, setLoadingServicos] = useState(false);

    const [categoriaId, setCategoriaId] = useState('');
    const [produtoId, setProdutoId] = useState('');
    const [servicoId, setServicoId] = useState('');
    const [servicoSelecionado, setServicoSelecionado] = useState(null);
    const [hierarchyError, setHierarchyError] = useState('');

    const prevCartCountRef = useRef(cartItemCount);

    useEffect(() => {
        if (cartItemCount > prevCartCountRef.current) {
            setCategoriaId('');
            setProdutoId('');
            setServicoId('');
            setServicoSelecionado(null);
            setProdutos([]);
            setServicos([]);
        }
        prevCartCountRef.current = cartItemCount;
    }, [cartItemCount]);

    useEffect(() => {
        const fetchCategorias = async () => {
            setLoadingCategorias(true);
            try {
                const data = await CatalogHierarchyService.getCategorias();
                setCategorias(data);
            } catch (err) {
                console.error('Erro ao buscar categorias:', err);
                setHierarchyError(sharedLanguage.shared_api_error_load || 'Erro ao carregar categorias');
            } finally {
                setLoadingCategorias(false);
            }
        };
        fetchCategorias();
    }, [sharedLanguage]);

    const handleCategoriaChange = async (event) => {
        const catId = event.target.value;
        setCategoriaId(catId);
        setProdutoId('');
        setServicoId('');
        setServicoSelecionado(null);
        setProdutos([]);
        setServicos([]);

        if (!catId) return;

        setLoadingProdutos(true);
        try {
            const data = await CatalogHierarchyService.getProdutosByCategoria(catId);
            setProdutos(data);
        } catch (err) {
            console.error('Erro ao buscar produtos:', err);
        } finally {
            setLoadingProdutos(false);
        }
    };

    const handleProdutoChange = async (event) => {
        const prodId = event.target.value;
        setProdutoId(prodId);
        setServicoId('');
        setServicoSelecionado(null);
        setServicos([]);

        if (!prodId) return;

        setLoadingServicos(true);
        try {
            const data = await CatalogHierarchyService.getServicosByProduto(prodId);
            setServicos(data);
        } catch (err) {
            console.error('Erro ao buscar serviços:', err);
        } finally {
            setLoadingServicos(false);
        }
    };

    const handleServicoChange = (event) => {
        const srvId = event.target.value;
        setServicoId(srvId);
        
        const selected = servicos.find(s => String(s.id) === String(srvId));
        if (selected) {
            const cat = categorias.find(c => c.id === categoriaId);
            const prod = produtos.find(p => p.id === produtoId);
            setServicoSelecionado({
                id: selected.id,
                nome: selected.nome,
                descricao: selected.nome, // Preenche ambos para compatibilidade
                categoria_id: categoriaId,
                produto_id: produtoId,
                categoria_nome: cat ? cat.nome : '',
                produto_nome: prod ? prod.nome : ''
            });
        } else {
            setServicoSelecionado(null);
        }
    };

    const breadcrumbItems = useMemo(
        () => [
            { label: clientLanguage.client_quote_home_breadcrumb, path: '/bepi/client' },
            { label: clientLanguage.client_quote_quote_breadcrumb },
        ],
        [clientLanguage]
    );

    const cartLabel = useMemo(
        () =>
            String(clientLanguage.client_quote_search_cart_btn || '')
                .replace('#vr0#', cartItemCount),
        [cartItemCount, clientLanguage]
    );

    return (
        <BreadcrumbLayout
            title={clientLanguage.client_quote_page_title}
            breadcrumbItems={breadcrumbItems}
            currentLanguage={currentLanguage}
            showBackButton
        >
            <ClientHelpModal view='VIEW_CLIENT_ESTIMATE' currentLanguage={currentLanguage} />

            <MainContent>
                <SearchHeaderSection>
                    <ContentAlignedContainer>
                        <SearchHero>
                            <BuilderContainer>
                                <BuilderCard>
                                    <StepHeader>
                                        <StepTitle>Nova Solicitação de Orçamento</StepTitle>
                                        <StepSubtitle>Identifique a categoria e o modelo do equipamento para iniciar.</StepSubtitle>
                                    </StepHeader>

                                    <FormRow>
                                        <StyledLabel>
                                            <StepIndicator active={true}>1</StepIndicator>
                                            Selecione a Categoria
                                        </StyledLabel>
                                        <StyledFormControl variant="outlined" size="medium">
                                            <Select
                                                value={categoriaId}
                                                onChange={handleCategoriaChange}
                                                displayEmpty
                                                disabled={loadingCategorias}
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span style={{ color: '#9CA3AF' }}>{loadingCategorias ? 'Carregando...' : 'Escolha uma categoria...'}</span>;
                                                    }
                                                    const cat = categorias.find(c => c.id === selected);
                                                    return cat ? cat.nome : '';
                                                }}
                                            >
                                                {categorias.map(cat => (
                                                    <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        </StyledFormControl>
                                    </FormRow>

                                    {categoriaId && (
                                        <FormRow style={{ animationDelay: '0.1s' }}>
                                            <StyledLabel>
                                                <StepIndicator active={true}>2</StepIndicator>
                                                Selecione o Produto/Família
                                            </StyledLabel>
                                            <StyledFormControl variant="outlined" size="medium">
                                                <Select
                                                    value={produtoId}
                                                    onChange={handleProdutoChange}
                                                    displayEmpty
                                                    disabled={loadingProdutos}
                                                    renderValue={(selected) => {
                                                        if (!selected) {
                                                            return <span style={{ color: '#9CA3AF' }}>{loadingProdutos ? 'Carregando...' : (produtos.length === 0 ? 'Nenhum modelo disponível' : 'Escolha um modelo/família...')}</span>;
                                                        }
                                                        const prod = produtos.find(p => p.id === selected);
                                                        return prod ? prod.nome : '';
                                                    }}
                                                >
                                                    {produtos.map(prod => (
                                                        <MenuItem key={prod.id} value={prod.id}>{prod.nome}</MenuItem>
                                                    ))}
                                                </Select>
                                            </StyledFormControl>
                                        </FormRow>
                                    )}

                                    {produtoId && (
                                        <FormRow style={{ animationDelay: '0.2s' }}>
                                            <StyledLabel>
                                                <StepIndicator active={true}>3</StepIndicator>
                                                Selecione o Serviço/Especificação
                                            </StyledLabel>
                                            <StyledFormControl variant="outlined" size="medium">
                                                <Select
                                                    value={servicoId}
                                                    onChange={handleServicoChange}
                                                    displayEmpty
                                                    disabled={loadingServicos}
                                                    renderValue={(selected) => {
                                                        if (!selected) {
                                                            return <span style={{ color: '#9CA3AF' }}>{loadingServicos ? 'Carregando...' : (servicos.length === 0 ? 'Nenhum serviço disponível' : 'Especifique o serviço/equipamento...')}</span>;
                                                        }
                                                        const srv = servicos.find(s => s.id === selected);
                                                        return srv ? srv.nome : '';
                                                    }}
                                                >
                                                    {servicos.map(srv => (
                                                        <MenuItem key={srv.id} value={srv.id}>{srv.nome}</MenuItem>
                                                    ))}
                                                </Select>
                                            </StyledFormControl>
                                        </FormRow>
                                    )}
                                    
                                    <Box mt={3}>
                                        <ContinueButton 
                                            disabled={!servicoSelecionado}
                                            onClick={() => onServiceSelect(servicoSelecionado)}
                                            endIcon={<PlayArrowIcon />}
                                            disableElevation
                                        >
                                            Continuar Orçamento
                                        </ContinueButton>
                                    </Box>
                                </BuilderCard>
                            </BuilderContainer>
                        </SearchHero>
                    </ContentAlignedContainer>
                </SearchHeaderSection>

                {loading || error || hierarchyError ? (
                    <FeedbackState
                        loading={loading}
                        error={error || hierarchyError}
                        loadingMessage={sharedLanguage.shared_equip_form_loading_msg}
                    />
                ) : null}

                {!loading && !error && !hierarchyError ? (
                    <ContentAlignedContainer>
                        <ServicesGrid>
                            {cartItemCount > 0 ? (
                                <CartShortcutButton type='button' onClick={onViewCart} style={{ gridColumn: '1 / -1', maxWidth: 300, margin: '20px auto 0' }}>
                                    <ServiceCardTitle>
                                        <ShoppingCartIcon fontSize='small' />
                                        {cartLabel}
                                    </ServiceCardTitle>
                                </CartShortcutButton>
                            ) : null}
                        </ServicesGrid>
                    </ContentAlignedContainer>
                ) : null}
            </MainContent>
        </BreadcrumbLayout>
    );
}

ClientQuoteSearchView.propTypes = {
    cartItemCount: PropTypes.number,
    onServiceSelect: PropTypes.func.isRequired,
    onViewCart: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    currentLanguage: PropTypes.object.isRequired,
};

ClientQuoteSearchView.defaultProps = {
    cartItemCount: 0,
    loading: false,
    error: '',
};

export default ClientQuoteSearchView;
